import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { axios } from '../../context/AuthContext';
import { notifyWeeklyProgramUpdated } from '../../services/weeklyProgramRefresh';
import './DersProgramiIceAktar.css';

const IMPORT_API = '/haftalik-program/ice-aktar';
const MAX_FILE_SIZE = 15 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['xlsx', 'xls', 'csv', 'pdf'];
const DAY_OPTIONS = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

const asArray = value => Array.isArray(value) ? value : [];
const getPayload = response => response?.data?.data ?? response?.data ?? {};
const getErrorPayload = error => error?.response?.data?.data ?? error?.response?.data ?? {};
const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback
);

const issueText = issue => {
  if (typeof issue === 'string') return issue;
  return issue?.message || issue?.mesaj || issue?.error || JSON.stringify(issue);
};

const normalizeIssues = value => {
  if (!value) return [];
  return (Array.isArray(value) ? value : [value]).map(issueText).filter(Boolean).map(String);
};

const referenceId = (item, type) => item?.id
  ?? item?.[`${type}Id`]
  ?? item?.[`${type}_id`]
  ?? item?.value
  ?? '';

const referenceName = (item, type) => item?.name
  ?? item?.[`${type}Name`]
  ?? item?.[`${type}_name`]
  ?? item?.ad
  ?? item?.label
  ?? '';

const normalizeReferences = references => ({
  classes: asArray(references?.classes || references?.siniflar).map(item => ({
    ...item,
    id: referenceId(item, 'class'),
    name: referenceName(item, 'class'),
  })),
  teachers: asArray(references?.teachers || references?.ogretmenler).map(item => ({
    ...item,
    id: referenceId(item, 'teacher'),
    name: referenceName(item, 'teacher'),
  })),
  subjects: asArray(references?.subjects || references?.dersler).map(item => ({
    ...item,
    id: referenceId(item, 'subject'),
    name: referenceName(item, 'subject'),
  })),
});

const normalizeRow = (row, index) => {
  const errors = normalizeIssues(row?.errors || row?.hatalar);
  if (row?.valid === false && errors.length === 0) errors.push('Satır doğrulanamadı.');
  return {
    ...row,
    _key: row?._key || row?.rowId || row?.rowIndex || row?.rowNumber || `row-${index}`,
    classId: row?.classId ?? '',
    className: row?.className ?? '',
    teacherId: row?.teacherId ?? '',
    teacherName: row?.teacherName ?? '',
    subjectId: row?.subjectId ?? '',
    subjectName: row?.subjectName ?? '',
    dayIndex: row?.dayIndex ?? '',
    dayName: row?.dayName ?? '',
    startTime: row?.startTime ?? '',
    endTime: row?.endTime ?? '',
    slotIndex: row?.slotIndex ?? '',
    room: row?.room ?? '',
    errors,
    warnings: normalizeIssues(row?.warnings || row?.uyarilar),
    valid: row?.valid !== false && errors.length === 0,
  };
};

const summaryValue = (summary, keys, fallback = 0) => {
  for (const key of keys) {
    if (summary?.[key] !== undefined && summary?.[key] !== null) return Number(summary[key]) || 0;
  }
  return fallback;
};

const normalizePreview = rawPayload => {
  const payload = rawPayload?.data ?? rawPayload ?? {};
  const rows = asArray(payload.rows || payload.satirlar).map(normalizeRow);
  const summary = payload.summary || payload.ozet || {};
  return {
    rows,
    references: normalizeReferences(payload.references || payload.referanslar || {}),
    summary: {
      totalRows: summaryValue(summary, ['totalRows', 'toplamSatir', 'total'], rows.length),
      validRows: summaryValue(summary, ['validRows', 'gecerliSatir', 'valid'], rows.filter(row => row.valid).length),
      invalidRows: summaryValue(summary, ['invalidRows', 'hataliSatir', 'invalid'], rows.filter(row => !row.valid).length),
      warningCount: summaryValue(summary, ['warningCount', 'uyariSayisi', 'warnings'], rows.reduce((sum, row) => sum + row.warnings.length, 0)),
      classCount: summaryValue(summary, ['classCount', 'sinifSayisi', 'classes']),
      teacherCount: summaryValue(summary, ['teacherCount', 'ogretmenSayisi', 'teachers']),
    },
  };
};

const findValidationRows = payload => {
  const root = payload?.data ?? payload ?? {};
  const candidates = root.rows || root.satirlar || root.rowErrors || root.satirHatalari || root.errors;
  return asArray(candidates);
};

const mergeValidationErrors = (currentRows, payload) => {
  const serverRows = findValidationRows(payload);
  if (serverRows.length === 0) return currentRows;
  if (currentRows.length === 0) return serverRows.map(normalizeRow);

  return currentRows.map((row, index) => {
    const rowNumber = row.rowIndex ?? row.rowNumber ?? index;
    const match = serverRows.find((item, serverIndex) => {
      const serverNumber = item?.rowIndex ?? item?.rowNumber ?? item?.row ?? item?.index ?? serverIndex;
      return String(serverNumber) === String(rowNumber) || Number(serverNumber) - 1 === Number(rowNumber);
    });
    if (!match) return row;
    const errors = normalizeIssues(match.errors || match.hatalar || match.messages || match.message || match.mesaj);
    const warnings = normalizeIssues(match.warnings || match.uyarilar);
    return {
      ...row,
      ...match,
      _key: row._key,
      errors: errors.length ? errors : row.errors,
      warnings: warnings.length ? warnings : row.warnings,
      valid: false,
    };
  });
};

const fileExtension = file => String(file?.name || '').split('.').pop().toLocaleLowerCase('tr-TR');

const validateFile = file => {
  if (!file) return 'Bir dosya seçin.';
  if (!ACCEPTED_EXTENSIONS.includes(fileExtension(file))) return 'Yalnızca .xlsx, .xls, .csv veya .pdf dosyaları yüklenebilir.';
  if (file.size > MAX_FILE_SIZE) return 'Dosya boyutu en fazla 15 MB olabilir.';
  return '';
};

const editableRowsForRequest = rows => rows.map(row => ({
  classId: row.classId,
  className: row.className,
  teacherId: row.teacherId,
  teacherName: row.teacherName,
  subjectId: row.subjectId,
  subjectName: row.subjectName,
  dayIndex: row.dayIndex === '' ? '' : Number(row.dayIndex),
  dayName: row.dayName,
  startTime: row.startTime,
  endTime: row.endTime,
  slotIndex: row.slotIndex === '' ? '' : Number(row.slotIndex),
  room: row.room,
  errors: row.errors,
  warnings: row.warnings,
  valid: row.valid,
}));

const fieldIssueTerms = {
  classId: ['sınıf', 'sinif', 'class', 'çakış'],
  teacherId: ['öğretmen', 'ogretmen', 'teacher', 'çakış'],
  subjectId: ['ders', 'subject'],
  dayIndex: ['gün', 'gun', 'day', 'çakış'],
  startTime: ['saat', 'başlangıç', 'baslangic', 'time', 'çakış'],
  endTime: ['saat', 'bitiş', 'bitis', 'time', 'çakış'],
  room: ['oda', 'derslik', 'salon', 'room', 'çakış'],
};

const requiredErrors = row => {
  const errors = [];
  if (!row.classId) errors.push('Sınıf seçilmelidir.');
  if (!row.teacherId) errors.push('Öğretmen seçilmelidir.');
  if (!row.subjectId) errors.push('Ders seçilmelidir.');
  if (row.dayIndex === '' || row.dayIndex === null || row.dayIndex === undefined) errors.push('Gün seçilmelidir.');
  if (!row.startTime) errors.push('Başlangıç saati girilmelidir.');
  if (!row.endTime) errors.push('Bitiş saati girilmelidir.');
  if (row.startTime && row.endTime && row.endTime <= row.startTime) errors.push('Bitiş saati başlangıç saatinden büyük olmalıdır.');
  return errors;
};

function ReplaceConfirmModal({ publishing, onCancel, onConfirm }) {
  return (
    <div className="modal-backdrop" role="presentation" onClick={publishing ? undefined : onCancel}>
      <div className="modal schedule-import-confirm" role="dialog" aria-modal="true" aria-labelledby="replace-confirm-title" onClick={event => event.stopPropagation()}>
        <div className="modal-title" id="replace-confirm-title">Mevcut programı değiştir</div>
        <p>Mevcut yayımlanmış ders programı değiştirilecek. İşlem öncesinde sistem tarafından otomatik yedek alınacaktır. Devam etmek istiyor musunuz?</p>
        <div className="schedule-import-modal-actions">
          <button className="btn btn-secondary" type="button" onClick={onCancel} disabled={publishing}>Vazgeç</button>
          <button className="btn btn-danger" type="button" onClick={onConfirm} disabled={publishing}>
            {publishing ? 'Yayımlanıyor...' : 'Evet, Programı Değiştir'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DersProgramiIceAktar() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('append');
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const [apiError, setApiError] = useState(null);

  const rows = preview?.rows || [];
  const references = preview?.references || { classes: [], teachers: [], subjects: [] };
  const hasInvalidRows = rows.some(row => !row.valid || row.errors.length > 0);

  const displayedSummary = useMemo(() => {
    if (!preview) return null;
    return {
      ...preview.summary,
      validRows: rows.filter(row => row.valid && row.errors.length === 0).length,
      invalidRows: rows.filter(row => !row.valid || row.errors.length > 0).length,
      warningCount: rows.reduce((sum, row) => sum + row.warnings.length, 0),
    };
  }, [preview, rows]);

  const resetImport = () => {
    setFile(null);
    setPreview(null);
    setApiError(null);
    setMode('append');
    if (inputRef.current) inputRef.current.value = '';
  };

  const selectFile = selectedFile => {
    const validationMessage = validateFile(selectedFile);
    if (validationMessage) {
      setFile(null);
      setPreview(null);
      setApiError({ message: validationMessage });
      toast.error(validationMessage);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setFile(selectedFile);
    setPreview(null);
    setApiError(null);
  };

  const handleModeChange = nextMode => {
    setMode(nextMode);
    setPreview(null);
    setApiError(null);
  };

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${IMPORT_API}/sablon`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'ders-programi-ice-aktarma-sablonu.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Excel şablonu indirilemedi'));
    } finally {
      setDownloading(false);
    }
  };

  const apply422Errors = error => {
    const payload = getErrorPayload(error);
    const normalized = normalizePreview(payload);
    setPreview(current => {
      const base = current || normalized;
      return {
        ...base,
        references: normalized.references.classes.length || normalized.references.teachers.length || normalized.references.subjects.length
          ? normalized.references
          : base.references,
        summary: normalized.rows.length ? normalized.summary : base.summary,
        rows: mergeValidationErrors(base.rows || [], payload),
      };
    });
  };

  const showApiError = (error, fallback) => {
    const message = getErrorMessage(error, fallback);
    const isPdf = fileExtension(file) === 'pdf';
    setApiError({
      message,
      suggestion: isPdf
        ? 'PDF metin olarak okunamıyorsa Excel şablonunu indirip .xlsx biçiminde yüklemeyi deneyin.'
        : '',
    });
    toast.error(message);
  };

  const handlePreview = async () => {
    const validationMessage = validateFile(file);
    if (validationMessage) return selectFile(file);
    setPreviewing(true);
    setApiError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);
      const response = await axios.post(`${IMPORT_API}/onizle`, formData);
      setPreview(normalizePreview(getPayload(response)));
    } catch (error) {
      if (error.response?.status === 422) apply422Errors(error);
      showApiError(error, 'Dosya önizlenemedi');
    } finally {
      setPreviewing(false);
    }
  };

  const updateRow = (rowIndex, field, value, pairedField, pairedValue) => {
    setPreview(current => {
      if (!current) return current;
      const nextRows = current.rows.map((row, index) => {
        if (index !== rowIndex) return row;
        const next = { ...row, [field]: value };
        if (pairedField) next[pairedField] = pairedValue;
        const terms = fieldIssueTerms[field] || [];
        const unresolved = row.errors.filter(error => {
          const normalized = error.toLocaleLowerCase('tr-TR');
          return !terms.some(term => normalized.includes(term));
        });
        next.errors = [...new Set([...unresolved, ...requiredErrors(next)])];
        next.valid = next.errors.length === 0;
        return next;
      });
      return { ...current, rows: nextRows };
    });
  };

  const publish = async () => {
    setPublishing(true);
    setApiError(null);
    try {
      await axios.post(`${IMPORT_API}/yayinla`, {
        mode,
        confirmReplace: mode === 'replace',
        programName: 'İçe Aktarılan Ders Programı',
        rows: editableRowsForRequest(rows),
      });
      toast.success('Ders programı başarıyla yayımlandı');
      resetImport();
      try {
        await axios.get('/haftalik-program/setup');
      } catch (refreshError) {
        toast.error(getErrorMessage(refreshError, 'Ders programı yenilenemedi'));
      }
      notifyWeeklyProgramUpdated();
    } catch (error) {
      if (error.response?.status === 422) apply422Errors(error);
      showApiError(error, 'Ders programı yayımlanamadı');
    } finally {
      setPublishing(false);
      setReplaceConfirmOpen(false);
    }
  };

  const requestPublish = () => {
    if (mode === 'replace') setReplaceConfirmOpen(true);
    else publish();
  };

  const summaryCards = displayedSummary ? [
    ['Toplam satır', displayedSummary.totalRows, 'blue'],
    ['Geçerli satır', displayedSummary.validRows, 'green'],
    ['Hatalı satır', displayedSummary.invalidRows, 'red'],
    ['Uyarı sayısı', displayedSummary.warningCount, 'orange'],
    ['Sınıf sayısı', displayedSummary.classCount, 'purple'],
    ['Öğretmen sayısı', displayedSummary.teacherCount, 'cyan'],
  ] : [];

  return (
    <section className="card schedule-import-card animate-fadeInUp" aria-labelledby="schedule-import-title">
      <div className="schedule-import-header">
        <div>
          <h3 id="schedule-import-title">📥 Ders Programı İçe Aktar</h3>
          <p>Excel, CSV veya metin tabanlı PDF dosyasını kontrol edip mevcut haftalık program altyapısına yayımlayın.</p>
        </div>
        <button className="btn btn-secondary" type="button" onClick={downloadTemplate} disabled={downloading}>
          {downloading ? 'İndiriliyor...' : '⬇ Excel Şablonunu İndir'}
        </button>
      </div>

      <div className="schedule-import-method" role="radiogroup" aria-label="İçe aktarma yöntemi">
        <label className={mode === 'append' ? 'selected' : ''}>
          <input type="radio" name="import-mode" value="append" checked={mode === 'append'} onChange={() => handleModeChange('append')} />
          <span><strong>Mevcut programa ekle</strong><small>Yeni satırlar mevcut programa eklenir.</small></span>
        </label>
        <label className={mode === 'replace' ? 'selected replace' : ''}>
          <input type="radio" name="import-mode" value="replace" checked={mode === 'replace'} onChange={() => handleModeChange('replace')} />
          <span><strong>Mevcut programı değiştir</strong><small>Yayımlanmış program yedeklenerek değiştirilir.</small></span>
        </label>
      </div>

      {mode === 'replace' && (
        <div className="alert alert-warning schedule-import-replace-warning" role="alert">
          ⚠️ Mevcut ders programı yayımlama sırasında değiştirilecektir. Devam etmeden önce önizlemeyi dikkatle kontrol edin.
        </div>
      )}

      <div
        className={`schedule-import-dropzone${dragActive ? ' dragging' : ''}`}
        onDragEnter={event => { event.preventDefault(); setDragActive(true); }}
        onDragOver={event => event.preventDefault()}
        onDragLeave={event => { event.preventDefault(); setDragActive(false); }}
        onDrop={event => {
          event.preventDefault();
          setDragActive(false);
          selectFile(event.dataTransfer.files?.[0]);
        }}
      >
        <input
          ref={inputRef}
          id="schedule-import-file"
          type="file"
          accept=".xlsx,.xls,.csv,.pdf"
          onChange={event => selectFile(event.target.files?.[0])}
        />
        <div className="schedule-import-upload-icon">⇧</div>
        <strong>{file ? file.name : 'Dosyayı buraya sürükleyin'}</strong>
        <span>{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'veya bilgisayarınızdan bir dosya seçin'}</span>
        <label className="btn btn-secondary" htmlFor="schedule-import-file">Dosya Seç</label>
        <small>.xlsx, .xls, .csv, .pdf · En fazla 15 MB</small>
      </div>

      <div className="alert alert-info schedule-import-pdf-note">
        ℹ️ PDF desteği yalnızca metin içeren PDF’ler içindir. Taranmış veya görüntü biçimindeki PDF’ler okunamayabilir; bu durumda Excel şablonunu kullanın.
      </div>

      {apiError && (
        <div className="alert alert-danger schedule-import-api-error" role="alert">
          <div><strong>{apiError.message}</strong>{apiError.suggestion && <span>{apiError.suggestion}</span>}</div>
        </div>
      )}

      <div className="schedule-import-primary-actions">
        {file && <button className="btn btn-secondary" type="button" onClick={resetImport}>Temizle</button>}
        <button className="btn btn-primary" type="button" onClick={handlePreview} disabled={!file || previewing}>
          {previewing ? 'Önizleniyor...' : 'Dosyayı Önizle'}
        </button>
      </div>

      {preview && (
        <div className="schedule-import-preview">
          <div className="schedule-import-summary">
            {summaryCards.map(([label, value, color]) => (
              <div className={`schedule-import-summary-card ${color}`} key={label}>
                <span>{label}</span><strong>{value}</strong>
              </div>
            ))}
          </div>

          <div className="schedule-import-table-heading">
            <div><h4>Önizleme ve Düzeltme</h4><p>Hatalı alanları tablonun üzerinden düzeltebilirsiniz.</p></div>
            <span className={`badge ${hasInvalidRows ? 'badge-danger' : 'badge-success'}`}>
              {hasInvalidRows ? 'Düzeltme gerekli' : 'Yayımlamaya hazır'}
            </span>
          </div>

          <div className="table-wrapper schedule-import-table-wrap">
            <table className="table schedule-import-table">
              <thead>
                <tr><th>#</th><th>Sınıf</th><th>Öğretmen</th><th>Ders</th><th>Gün</th><th>Başlangıç</th><th>Bitiş</th><th>Slot</th><th>Derslik</th><th>Hata / Uyarı</th></tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const rowState = row.errors.length ? 'invalid' : row.warnings.length ? 'warning' : 'valid';
                  return (
                    <tr className={rowState} key={row._key}>
                      <td>{row.rowNumber || row.rowIndex || index + 1}</td>
                      <td>
                        <select className="form-input" aria-label={`${index + 1}. satır sınıf`} value={row.classId} onChange={event => {
                          const selected = references.classes.find(item => String(item.id) === event.target.value);
                          updateRow(index, 'classId', event.target.value, 'className', selected?.name || '');
                        }}>
                          <option value="">Sınıf seçin</option>
                          {references.classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="form-input" aria-label={`${index + 1}. satır öğretmen`} value={row.teacherId} onChange={event => {
                          const selected = references.teachers.find(item => String(item.id) === event.target.value);
                          updateRow(index, 'teacherId', event.target.value, 'teacherName', selected?.name || '');
                        }}>
                          <option value="">Öğretmen seçin</option>
                          {references.teachers.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="form-input" aria-label={`${index + 1}. satır ders`} value={row.subjectId} onChange={event => {
                          const selected = references.subjects.find(item => String(item.id) === event.target.value);
                          updateRow(index, 'subjectId', event.target.value, 'subjectName', selected?.name || '');
                        }}>
                          <option value="">Ders seçin</option>
                          {references.subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <select className="form-input" aria-label={`${index + 1}. satır gün`} value={row.dayIndex} onChange={event => updateRow(index, 'dayIndex', event.target.value, 'dayName', event.target.value === '' ? '' : DAY_OPTIONS[Number(event.target.value)])}>
                          <option value="">Gün seçin</option>
                          {DAY_OPTIONS.map((dayName, dayIndex) => <option key={dayName} value={dayIndex}>{dayName}</option>)}
                        </select>
                      </td>
                      <td><input className="form-input" aria-label={`${index + 1}. satır başlangıç`} type="time" value={row.startTime} onChange={event => updateRow(index, 'startTime', event.target.value)} /></td>
                      <td><input className="form-input" aria-label={`${index + 1}. satır bitiş`} type="time" value={row.endTime} onChange={event => updateRow(index, 'endTime', event.target.value)} /></td>
                      <td>{row.slotIndex === '' ? '-' : row.slotIndex}</td>
                      <td><input className="form-input" aria-label={`${index + 1}. satır derslik`} value={row.room} onChange={event => updateRow(index, 'room', event.target.value)} /></td>
                      <td className="schedule-import-issues">
                        {row.errors.map((error, issueIndex) => <div className="error" key={`e-${issueIndex}`}>✕ {error}</div>)}
                        {row.warnings.map((warning, issueIndex) => <div className="warning" key={`w-${issueIndex}`}>⚠ {warning}</div>)}
                        {!row.errors.length && !row.warnings.length && <span className="ok">✓ Geçerli</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="schedule-import-publish-row">
            {hasInvalidRows && <span>Yayımlamak için tüm satır hatalarını düzeltin.</span>}
            <button className="btn btn-primary" type="button" onClick={requestPublish} disabled={publishing || rows.length === 0 || hasInvalidRows}>
              {publishing ? 'Yayımlanıyor...' : 'Yayımla'}
            </button>
          </div>
        </div>
      )}

      {replaceConfirmOpen && (
        <ReplaceConfirmModal publishing={publishing} onCancel={() => setReplaceConfirmOpen(false)} onConfirm={publish} />
      )}
    </section>
  );
}
